import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";

interface IdleTimeoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignOut: () => void;
}

const IdleTimeoutModal: React.FC<IdleTimeoutModalProps> = ({ isOpen, onClose, onSignOut }) => {
  const [secondsLeft, setSecondsLeft] = useState(60); // Contagem regressiva de 60 segundos
  const [isLoading, setIsLoading] = useState(false); // Estado para controlar o loading
  const [loadingTime, setLoadingTime] = useState(3); // Tempo de loading (3 segundos)
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioMP3 = require("./../assets/alert-signout.mp3");

  useEffect(() => {
    audioRef.current!.play()
    let countdownTimer: NodeJS.Timeout;
    let loadingTimer: NodeJS.Timeout;

    if (isOpen && secondsLeft > 0) {
      // Se o modal estiver aberto e o contador for maior que 0
      countdownTimer = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimer); // Interrompe a contagem ao atingir 0
            setIsLoading(true); // Inicia o loading de 3 segundos
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    if (isLoading) {
      // Quando o loading começar, cria um timer para o loading de 3 segundos
      loadingTimer = setInterval(() => {
        setLoadingTime((prev) => {
          if (prev <= 1) {
            onSignOut(); // Realiza o logout
            onClose(); // Fecha o modal
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    // Limpar os timers quando o modal for fechado ou quando o loading terminar
    return () => {
      if (countdownTimer) clearInterval(countdownTimer);
      //  if (loadingTimer) clearInterval(loadingTimer);
    };
  }, [isOpen, secondsLeft, isLoading, onSignOut, onClose]);

  if (!isOpen) return null; // Se o modal não estiver aberto, não renderiza nada

  return ReactDOM.createPortal(
    <div style={modalOverlayStyle} onClick={(e) => e.stopPropagation()}>
      <audio ref={audioRef} src={audioMP3} />
      <div style={modalContentStyle}>
        <h2 style={titleStyle}>Você será desconectado em</h2>
        <div style={circleStyle}>
          <span style={timerStyle}>{secondsLeft}</span>
        </div>
        <h2 style={titleStyle}>segundos!</h2>

        <div style={buttonContainerStyle}>
          {!isLoading && (
            <button
              style={signOutButtonStyle}
              onClick={() => {
                onSignOut();
                onClose();
              }}
            >
              Sair
            </button>
          )}
          <button style={returnButtonStyle} onClick={onClose}>
            Voltar
          </button>
        </div>
      </div>
    </div>,
    document.body // Renderiza o modal no body, fora da hierarquia normal do componente
  );
};

// Estilos do modal
const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 10000,
};

const modalContentStyle: React.CSSProperties = {
  backgroundColor: "white",
  padding: "30px",
  borderRadius: "12px",
  textAlign: "center",
  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.3)",
  maxWidth: "400px",
  width: "90%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  animation: "borderPulse 1s infinite", // Mantém a animação nas bordas
};

const titleStyle: React.CSSProperties = {
  fontSize: "1.5rem",
  fontWeight: "bold",
  marginBottom: "20px",
  color: "#333",
};

const circleStyle: React.CSSProperties = {
  width: "100px", // Pode ajustar o tamanho conforme desejado
  height: "100px",
  borderRadius: "50%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "black",
  animation: "backgroundPulse 1s infinite", // Animação no fundo do círculo
  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.3)",
  margin: "0 auto", // Centraliza horizontalmente
};

const timerStyle: React.CSSProperties = {
  fontSize: "2.5rem",
  fontWeight: "bold",
  color: "white",
  zIndex: 1,
};

const buttonContainerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-around",
  marginTop: "20px",
  gap: "10px",
};

const signOutButtonStyle: React.CSSProperties = {
  padding: "10px 20px",
  fontSize: "1rem",
  fontWeight: "bold",
  borderRadius: "5px",
  border: "none",
  backgroundColor: "#FF4500", // Botão vermelho
  color: "#fff",
  width: "100px", // Largura fixa para os botões serem iguais
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
};

const returnButtonStyle: React.CSSProperties = {
  padding: "10px 20px",
  fontSize: "1rem",
  fontWeight: "bold",
  borderRadius: "5px",
  border: "none",
  backgroundColor: "#4B0082", // Botão roxo
  color: "#fff",
  width: "100px", // Largura fixa para os botões serem iguais
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
};

// CSS para animação de fundo e borda pulsante
const cssAnimation = `
@keyframes backgroundPulse {
  0% { background-color: #FF4500; }
  50% { background-color: #4B0082; }
  100% { background-color: #FF4500; }
}

@keyframes borderPulse {
  0% { box-shadow: 0 0 10px 2px #FF4500; }
  50% { box-shadow: 0 0 10px 2px #4B0082; }
  100% { box-shadow: 0 0 10px 2px #FF4500; }
}
`;

// Injetando o CSS de animação no documento
const style = document.createElement("style");
style.type = "text/css";
style.innerHTML = cssAnimation;
document.head.appendChild(style);

export default IdleTimeoutModal;
